<template>
  <div class="ctx-tab">
    <section class="ctx-drawer__card" aria-labelledby="ctx-report-columns-heading">
      <div class="ctx-drawer__card-head">
        <span class="ctx-drawer__card-icon" aria-hidden="true">
          <v-icon icon="mdi-table-cog" size="18" />
        </span>
        <h3 id="ctx-report-columns-heading" class="ctx-drawer__card-title">
          Report columns
        </h3>
      </div>

      <p class="ctx-rc__intro">
        Choose which columns each report exports, and drag to reorder. Changes
        apply the next time a report is exported for this product -- nothing
        here affects reports already downloaded.
      </p>

      <div class="ctx-rc__type-picker" role="tablist" aria-label="Report type">
        <button
          v-for="type in REPORT_TYPES"
          :key="type.id"
          type="button"
          class="ctx-rc__type-chip"
          role="tab"
          :aria-selected="selectedReportType === type.id"
          :class="{ 'is-active': selectedReportType === type.id }"
          @click="selectedReportType = type.id"
        >
          {{ type.label }}
        </button>
      </div>

      <p v-if="selectedReportType === 'agentActivity'" class="ctx-rc__note">
        Agent activity rows summarize a whole day per agent, not one lead --
        so it only offers the fixed columns below, not custom lead fields.
      </p>

      <draggable
        v-model="localColumns"
        item-key="uid"
        handle=".ctx-rc__drag-handle"
        class="ctx-rc__list"
        ghost-class="ctx-rc__item--ghost"
        drag-class="ctx-rc__item--dragging"
      >
        <template #item="{ element }">
          <div class="ctx-rc__item" :class="{ 'ctx-rc__item--disabled': !element.enabled }">
            <span class="ctx-rc__drag-handle" aria-label="Drag to reorder" title="Drag to reorder">
              <v-icon icon="mdi-drag-vertical" size="18" />
            </span>
            <label class="ctx-rc__checkbox">
              <input type="checkbox" v-model="element.enabled" />
            </label>
            <span class="ctx-rc__item-label">{{ element.label }}</span>
            <span
              class="ctx-drawer__field-badge"
              :class="{ 'ctx-drawer__field-badge--id': element.kind === 'DYNAMIC' }"
            >
              {{ element.kind === 'DYNAMIC' ? 'custom field' : 'fixed' }}
            </span>
          </div>
        </template>
      </draggable>

      <p v-if="!localColumns.length" class="ctx-drawer__empty">
        No columns available for this report type yet.
      </p>

      <div class="ctx-rc__actions">
        <v-btn
          variant="flat"
          size="small"
          class="ctx-drawer__field-save"
          :loading="saving"
          @click="save"
        >
          Save {{ activeTypeLabel }} columns
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import { updateContextReportColumns } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { normalizeContextFieldDefinitions } from '@/utils/contexts'
import '@/styles/contexts/info-drawer.css'

defineOptions({ name: 'ContextReportColumnsTab' })

const props = defineProps({
  context: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'error'])

const REPORT_TYPES = [
  { id: 'callLog', label: 'Call log' },
  { id: 'statusHistory', label: 'Status history' },
  { id: 'agentActivity', label: 'Agent activity' },
  { id: 'leadDetail', label: 'Lead detail' },
]

// Mirrors the backend's own fixed-column catalogs (index.mjs's
// callLogFixedColumnCatalog/statusHistoryFixedColumnCatalog/
// agentActivityFixedColumnCatalog/leadDetailFixedColumnCatalog) -- labels
// only, this side never computes a value, just offers the picker. Kept in
// sync by hand since these change rarely; if a fixed column is ever added
// or renamed server-side, update both places.
const FIXED_COLUMN_CATALOGS = {
  callLog: [
    { id: 'datetime', label: 'Date / time' },
    { id: 'agent', label: 'Agent' },
    { id: 'duration', label: 'Duration (sec)' },
    { id: 'lead_status', label: 'Lead status (current)' },
    { id: 'lead_score', label: 'Lead score' },
    { id: 'call_type', label: 'Call type' },
    { id: 'direction', label: 'Direction' },
    { id: 'answered', label: 'Answered' },
  ],
  statusHistory: [
    { id: 'datetime', label: 'Date / time' },
    { id: 'prev_status', label: 'Previous status' },
    { id: 'new_status', label: 'New status' },
    { id: 'changed_by', label: 'Changed by' },
    { id: 'lead_score', label: 'Lead score' },
    { id: 'comments', label: 'Comments' },
  ],
  agentActivity: [
    { id: 'date', label: 'Date' },
    { id: 'agent', label: 'Agent' },
    { id: 'calls_placed', label: 'Calls placed' },
    { id: 'calls_connected', label: 'Calls connected' },
    { id: 'leads_touched', label: 'Leads touched' },
    { id: 'status_changes', label: 'Status changes' },
    { id: 'first_activity', label: 'First activity (UTC)' },
    { id: 'last_activity', label: 'Last activity (UTC)' },
  ],
  leadDetail: [
    { id: 'status', label: 'Status' },
    { id: 'lead_score', label: 'Lead score' },
    { id: 'assigned_agent', label: 'Assigned agent' },
    { id: 'calls_in_range', label: 'Calls in range' },
    { id: 'status_changes_in_range', label: 'Status changes in range' },
    { id: 'custom_fields_combined', label: 'Custom fields' },
  ],
}

// Exactly today's columns, exactly today's order -- matches
// DEFAULT_COLUMN_ORDER in index.mjs. Lead/Phone/Name/Email were deliberately
// dropped (2026-08-19): they duplicated the dynamic Contact Name/Contact
// Number/Contact Email columns already offered from custom_field_definitions.
// Outcome was removed from callLog entirely -- Answered already covers it.
const DEFAULT_COLUMN_ORDER = {
  callLog: ['datetime', 'agent', 'duration', 'lead_status', 'lead_score'],
  statusHistory: ['datetime', 'prev_status', 'new_status', 'changed_by', 'lead_score', 'comments'],
  agentActivity: ['date', 'agent', 'calls_placed', 'calls_connected', 'leads_touched', 'status_changes', 'first_activity', 'last_activity'],
  leadDetail: ['status', 'lead_score', 'assigned_agent', 'calls_in_range', 'status_changes_in_range', 'custom_fields_combined'],
}

const selectedReportType = ref('callLog')
const saving = ref(false)
const localColumns = ref([])
let uidCounter = 0

const activeTypeLabel = computed(
  () => REPORT_TYPES.find((t) => t.id === selectedReportType.value)?.label || 'these',
)

// Same as the backend's dynamicColumnCatalog: every registered field is
// offered, system fields included -- excluding is_system meant fields like
// Comments/Industry/Message/Business Type were never offered as a report
// column anywhere, since the fixed catalogs only actually cover
// contact_name/contact_number, not the rest of the system-field set.
// Internal fields stay hidden, same as everywhere else they're hidden from
// lead forms.
function dynamicColumnCatalog() {
  if (selectedReportType.value === 'agentActivity') return []
  const definitions = normalizeContextFieldDefinitions(props.context?.custom_field_definitions)
  return Object.entries(definitions)
    .filter(([, def]) => def?.enabled !== false && !def?.is_internal)
    .map(([key, def]) => ({ id: key, label: def?.label || key }))
}

// Same union-merge resolution as resolveReportColumns in index.mjs: saved
// config wins for order/visibility, anything new in the catalogs (a fixed
// column just shipped, a custom field just added) appends enabled, and any
// saved id no longer in either catalog silently drops.
function buildLocalColumns() {
  const reportType = selectedReportType.value
  const fixedCatalog = FIXED_COLUMN_CATALOGS[reportType] || []
  const fixedById = Object.fromEntries(fixedCatalog.map((c) => [c.id, c]))
  const dynamicCatalog = dynamicColumnCatalog()
  const dynamicById = Object.fromEntries(dynamicCatalog.map((c) => [c.id, c]))

  const saved = props.context?.report_column_config?.[reportType]
  const seen = new Set()
  const resolved = []

  if (Array.isArray(saved)) {
    for (const col of saved) {
      const catalog = col.kind === 'DYNAMIC' ? dynamicById : fixedById
      const entry = catalog[col.id]
      if (!entry) continue
      seen.add(`${col.kind}:${col.id}`)
      resolved.push({ uid: uidCounter++, id: col.id, kind: col.kind, label: entry.label, enabled: col.enabled !== false })
    }
  } else {
    for (const id of DEFAULT_COLUMN_ORDER[reportType] || []) {
      if (!fixedById[id]) continue
      seen.add(`FIXED:${id}`)
      resolved.push({ uid: uidCounter++, id, kind: 'FIXED', label: fixedById[id].label, enabled: true })
    }
  }
  for (const col of fixedCatalog) {
    if (!seen.has(`FIXED:${col.id}`)) resolved.push({ uid: uidCounter++, id: col.id, kind: 'FIXED', label: col.label, enabled: true })
  }
  for (const col of dynamicCatalog) {
    if (!seen.has(`DYNAMIC:${col.id}`)) resolved.push({ uid: uidCounter++, id: col.id, kind: 'DYNAMIC', label: col.label, enabled: true })
  }
  return resolved
}

watch(
  [selectedReportType, () => props.context?.context_id, () => props.context?.report_column_config],
  () => { localColumns.value = buildLocalColumns() },
  { immediate: true },
)

async function save() {
  if (!props.context?.context_id) return
  saving.value = true
  try {
    const response = await updateContextReportColumns({
      context_id: props.context.context_id,
      report_type: selectedReportType.value,
      columns: localColumns.value.map((c) => ({ id: c.id, kind: c.kind, enabled: c.enabled })),
    })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save report columns'))
      return
    }
    emit('saved', getApiMessage(response, `${activeTypeLabel.value} columns saved.`))
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.ctx-tab {
  max-width: 42rem;
}

.ctx-rc__intro {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: var(--tw-ink-muted, #6b7280);
}

.ctx-rc__type-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.ctx-rc__type-chip {
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--tw-border, #e5e7eb);
  background: transparent;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.ctx-rc__type-chip.is-active {
  background: var(--tw-accent, #4f46e5);
  border-color: var(--tw-accent, #4f46e5);
  color: #fff;
}

.ctx-rc__note {
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  color: var(--tw-ink-muted, #6b7280);
}

.ctx-rc__list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}

.ctx-rc__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--tw-border, #e5e7eb);
  border-radius: 0.5rem;
  background: var(--tw-surface, #fff);
}

.ctx-rc__item--disabled {
  opacity: 0.55;
}

.ctx-rc__item--ghost {
  opacity: 0.4;
}

.ctx-rc__item--dragging {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.ctx-rc__drag-handle {
  display: inline-flex;
  cursor: grab;
  color: var(--tw-ink-muted, #9ca3af);
  touch-action: none;
}

.ctx-rc__checkbox {
  display: inline-flex;
  align-items: center;
}

.ctx-rc__checkbox input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.ctx-rc__item-label {
  flex: 1;
  font-size: 0.87rem;
  font-weight: 500;
}

.ctx-rc__actions {
  display: flex;
  justify-content: flex-end;
}

.v-theme--dark .ctx-rc__type-chip {
  border-color: rgba(255, 255, 255, 0.14);
}

.v-theme--dark .ctx-rc__item {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}
</style>
